import {
    Category,
    CreateGroup,
    GroupCategoriesConfiguration,
    GroupListConfig,
    GroupProperties,
    GroupUserLevel,
    GroupWidget,
    jGroupList,
    jGroupLittle,
    jGroupView,
    ResetNotificationConfiguration,
    UpdateGroup,
    UserLevelAndRight,
} from '../../types/group';
import { JBaseApi } from '../base';
import { OF } from '../../util/network';
import {
    ApiReturn,
    ApiWrapper,
    BaseMessages,
    FormatList,
    jArticleLittle,
    jObjectLittle,
    jSpotAutocomplete,
    jUserLittle,
    PagingResults,
} from '../../types';
import { combineFormat } from '../../util/object';
import { FormatExtension } from '../../types/extensions';

export class JGroupApi extends JBaseApi {
    /**
     * autocomplete return a list of groups
     * @param query <string>
     */
    public autocomplete(query: string) {
        return this.network
            .post<Array<jSpotAutocomplete>>({ o: 'autocomplete', f: 'spot', q: query, idToUri: false } as OF)
            .then((res) => {
                return res.result;
            });
    }

    /**
     * Create a group from scratch
     * @param group
     * @param format return format
     * @returns
     */
    public create<T extends FormatList = undefined>(group: CreateGroup, format?: T) {
        return this.network.post<ApiReturn<T, jGroupLittle, jGroupList, jGroupView, jObjectLittle>>({
            o: 'spot',
            f: 'create',
            ...group,
            format,
        } as OF);
    }

    /**
     * Update a group
     * @param group group to update. The uri is mandatory and identify the group to update
     * @param format return format
     * @returns
     */
    public update<T extends FormatList = undefined>(group: UpdateGroup, format?: T) {
        // remove spotBanner from group because if provided, the spot banner image will be deleted
        const { spotBanner: _spotBanner, ...restGroup } = group;

        return this.network.postCSRF<ApiReturn<T, jGroupLittle, jGroupList, jGroupView, string>>({
            o: 'spot',
            f: 'update',
            ...restGroup,
            format,
        } as OF);
    }

    /**
     * Get a spot
     * @param idSpot
     * @param format
     * @returns
     */
    public getSpot<T extends FormatList = undefined>(idSpot: number | string, format?: T) {
        return this.network.post<ApiReturn<T, jGroupLittle, jGroupList, jGroupView, string>>({
            o: 'spot',
            f: 'getSpot',
            idSpot,
            format,
        } as OF);
    }

    /**
     * Get the properties of a group that are not within the model
     * @param idSpot
     * @returns
     */
    public getProperties(idSpot: number | string) {
        return this.network.post<GroupProperties>({
            o: 'spot',
            f: 'getProperties',
            idSpot,
        } as OF);
    }

    /**
     * Get the widgets of a group
     * @param idSpot
     * @returns
     */
    public getWidgets(idSpot: number | string) {
        return this.network.post<GroupProperties>({
            o: 'spot',
            f: 'getWidgets',
            idSpot,
        } as OF);
    }

    /**
     * Get the available applications that can be added to a group
     */
    public getApplications() {
        return this.network.post<Record<string, GroupWidget>>({ o: 'spot', f: 'get-available-widget' });
    }

    /**
     * Get the available group categories
     * @returns
     */
    public getCategories() {
        return this.network.post<Category[]>({ o: 'spot', f: 'get-categories' });
    }

    public getCategoriesConfiguration({ signal }: { signal?: AbortSignal } = {}): Promise<
        ApiWrapper<GroupCategoriesConfiguration>
    > {
        return this.network.post(
            {
                o: 'spot',
                f: 'getCategoriesConfiguration',
            } as OF,
            signal,
        );
    }

    /**
     * Get the members of a group
     * @param idSpot spot id
     * @param query query string to search within user names
     * @param limit limit per page
     * @param page page number
     * @return array of User
     */
    public getObjectListJamespotSpotMembers(idSpot: number | string, query?: string, limit?: number, page = 1) {
        return this.network.post<PagingResults<{ level: GroupUserLevel; user: jUserLittle }>>({
            o: 'objectListJamespot',
            f: 'SpotMembers',
            idSpot,
            limit,
            page,
            format: 'raw-little',
            query,
        } as OF);
    }

    public list<T extends jGroupList = jGroupList>(
        config: GroupListConfig,
        params?: {
            signal?: AbortSignal;
        },
    ): Promise<ApiWrapper<PagingResults<T>>> {
        return this.network.post(
            {
                o: 'objectList',
                f: 'jamespotSpotQueryList',
                ...config,
            } as OF,
            params?.signal,
        );
    }

    /**
     * Reset the notifications configuration for all users of the group
     * @param config configuration
     * @returns
     */
    public resetNotifications(config: ResetNotificationConfiguration) {
        return this.network.post<{ members: number; msg: string }>({
            o: 'spot',
            f: 'resetNotifications',
            ...config,
        });
    }

    /**
     * Get a spot
     * @param idSpot
     * @returns
     */
    public getUserLevelAndRight(idSpot: number | string): Promise<ApiWrapper<UserLevelAndRight, BaseMessages>> {
        return this.network.post<UserLevelAndRight>({
            o: 'spot',
            f: 'getUserLevelAndRight',
            idSpot,
        } as OF);
    }

    /**
     * Post a request to join a moderated group
     * @param idSpot
     * @returns
     */
    public groupRequest(idSpot: number | string): Promise<ApiWrapper<boolean, BaseMessages>> {
        return this.network.postCSRF<boolean>({
            o: 'spot',
            f: 'request',
            idSpot,
        } as OF);
    }

    /**
     * Join a public group
     * @param idSpot
     * @returns
     */
    public groupAddToFavorite(idSpot: number | string): Promise<ApiWrapper<boolean, BaseMessages>> {
        return this.network.postCSRF<boolean>({
            o: 'spot',
            f: 'addToFavorite',
            idSpot,
        } as OF);
    }

    public objectListSpotArticles<T extends jArticleLittle = jArticleLittle>(
        payload: {
            idSpot: number;
            format?: FormatList;
            limit?: number;
            page?: number;
            order?: string;
            tagConcepts?: string[];
            types?: string[];
        },
        params?: { signal?: AbortSignal; formatExtension?: FormatExtension },
    ): Promise<ApiWrapper<PagingResults<T>>> {
        return this.network.post(
            {
                o: 'objectList',
                f: 'spotArticles',
                ...payload,
                format: combineFormat(payload.format ?? 'raw-little', params?.formatExtension),
            } as OF,
            params?.signal,
        );
    }

    public changeMemberRole(payload: { idUser: number; role: number; idSpot: number }) {
        return this.network.post<string>({
            o: 'spot',
            f: 'changeRole',
            ...payload,
        } as OF);
    }

    public countMembers(payload: { uri: string }, signal?: AbortSignal): Promise<ApiWrapper<number>> {
        return this.network.postCSRF(
            {
                o: 'spot',
                f: 'countMembers',
                ...payload,
            } as OF,
            signal,
        );
    }
}
